'use client'

import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ChevronDown, ChevronRight, LogIn } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useFacilityBranding } from '@/contexts/SubdomainBrandingContext'
import { R, serif, PrintFrame, PrintButton, RecordHeader, StatRow, StandardStatement } from '../records/recordShared'
import type { PlanModule } from '@/hooks/useLearningRecord'
import { useTranslation } from 'react-i18next'
import { renderFormattedText, shouldShowAge } from '@/utils/textClean'

const ASSESSMENT_LABEL: Record<string, string> = {
  essay_per_module: 'Module essays (rubric-graded)',
  short_answer_only: 'Short written answers (rubric-graded)',
  capstone_only: 'Capstone essay (rubric-graded)',
  none: 'Mastery quizzes',
}

export default function CurriculumOutlinePage() {
  const { t } = useTranslation()
  const { classId } = useParams<{ classId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const branding = useFacilityBranding()
  const showAge = shouldShowAge(branding?.org_type)

  const [cls, setCls] = useState<{ name: string; subject_group: string | null; module_plan: PlanModule[]; essay_mode: string | null; target_age: number | null } | null>(null)
  const [loading, setLoading] = useState(true)
  const [openModules, setOpenModules] = useState<Set<number>>(new Set([0]))

  useEffect(() => {
    if (!classId) return
    let cancelled = false
    supabase.from('classes')
      .select('name, subject_group, module_plan, essay_mode, target_age')
      .eq('id', classId).maybeSingle()
      .then(({ data }) => { if (!cancelled) { setCls(data as any); setLoading(false) } })
    return () => { cancelled = true }
  }, [classId])

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1)
    } else {
      navigate('/platform/setup')
    }
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100dvh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontFamily: serif }}>
        Loading curriculum…
      </div>
    )
  }

  if (!cls) {
    // Anon viewers hit RLS on classes — offer sign-in (the "see every concept" pitch)
    return (
      <PrintFrame maxWidth={640}>
        <RecordHeader eyebrow="Still Waters Academy · Master Curriculum" title="See every concept before you enrol" />
        <p style={{ color: R.slate, fontSize: 15, lineHeight: 1.7 }}>
          Every Sage course publishes its complete scope — every module, lesson, objective, and the concepts taught in each.
          {!user && ' Create a free account to view this course outline in full.'}
        </p>
        <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
          {!user ? (
            <Link to="/auth/login" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: R.gold, color: '#fff', textDecoration: 'none', borderRadius: 10, padding: '12px 22px', fontSize: 14, fontWeight: 800 }}>
              <LogIn size={16} /> Sign in to view
            </Link>
          ) : (
            <span style={{ color: R.muted, fontSize: 14 }}>{t('common.course_not_found')}</span>
          )}
        </div>
        <StandardStatement />
      </PrintFrame>
    )
  }

  const plan = Array.isArray(cls.module_plan) ? cls.module_plan : []
  const totalLessons = plan.reduce((s, m) => s + (m.lessons?.length ?? 0), 0)
  const totalConcepts = plan.reduce((s, m) => s + (m.lessons ?? []).reduce((x, l) => x + (l.core_concepts?.length ?? 0), 0), 0)
  const toggle = (i: number) => setOpenModules(prev => {
    const next = new Set(prev)
    next.has(i) ? next.delete(i) : next.add(i)
    return next
  })

  return (
    <PrintFrame>
      <style>{`
        @media screen {
          .no-screen-collapsed { display: none !important; }
        }
        @media print {
          .no-screen-collapsed { display: block !important; }
        }
        @media screen and (max-width: 640px) {
          .record-page {
            padding: 20px 14px 44px !important;
          }
        }
      `}</style>
      {/* Toolbar — hidden in print */}
      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <button onClick={handleBack} style={{ background: 'none', border: 'none', color: R.slate, fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
          ← Back
        </button>
        <PrintButton />
      </div>

      <RecordHeader
        eyebrow="Still Waters Academy · Master Curriculum"
        title={cls.name}
        subtitle={`${cls.subject_group || 'General'}${showAge && cls.target_age ? ` · Recommended ${cls.target_age}+` : ''} · ${ASSESSMENT_LABEL[cls.essay_mode || 'none'] || 'Mastery quizzes'}`}
        right={<PrintButton />}
      />

      <StatRow items={[
        { label: 'Modules', value: plan.length },
        { label: 'Lessons', value: totalLessons },
        { label: 'Concepts taught', value: totalConcepts },
        { label: 'Mastery threshold', value: '80%' },
      ]} />

      {plan.map((m, mi) => {
        const open = openModules.has(mi)
        return (
          <div key={m.module_id || mi} className="print-block" style={{ marginBottom: 6 }}>
            <div className="module-header"
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '13px 14px',
                background: R.cream, border: `1px solid ${R.border}`, borderRadius: 10
              }}>
              <button className="no-print" onClick={() => toggle(mi)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: R.gold, display: 'flex', padding: 0 }}>
                {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              <div style={{ width: 26, height: 26, borderRadius: 8, background: R.gold, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900, flexShrink: 0 }}>
                {mi + 1}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 14.5, color: R.ink }}>{renderFormattedText(m.title)}</div>
                <div style={{ fontSize: 11.5, color: R.muted }}>{m.lessons?.length ?? 0} lessons</div>
              </div>
            </div>

            <div className={open ? 'print-block' : 'print-block no-screen-collapsed'} style={{ paddingLeft: 8, paddingTop: 6 }}>
                {(m.lessons ?? []).map((l, li) => (
                  <div key={l.lesson_id || li} className="print-block"
                    style={{ padding: '9px 12px', borderBottom: `1px solid ${R.border}` }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                      <span style={{ fontSize: 11, fontWeight: 900, color: R.gold, flexShrink: 0, width: 20 }}>
                        {mi + 1}.{li + 1}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: R.ink }}>{renderFormattedText(l.title)}</div>
                        {l.learning_objective && (
                          <div style={{ fontSize: 12, color: R.slate, marginTop: 2, lineHeight: 1.5 }}>
                            Objective: {renderFormattedText(l.learning_objective)}
                          </div>
                        )}
                        {/* Concept appendix — the curriculum-of-record claim, rendered */}
                        {!!l.core_concepts?.length && (
                          <ul style={{ margin: '6px 0 0', paddingLeft: 18, listStyle: 'none' }}>
                            {l.core_concepts.map((c, ci) => (
                              <li key={ci} style={{ fontSize: 11.5, color: R.slate, lineHeight: 1.55, position: 'relative', marginBottom: 2 }}>
                                <span style={{ position: 'absolute', left: -13, color: R.goldSoft }}>•</span>{renderFormattedText(c)}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 800, color: R.muted, flexShrink: 0 }}>
                        {l.core_concepts?.length ?? 0} concepts
                      </span>
                    </div>
                  </div>
                ))}
              </div>
          </div>
        )
      })}

      <StandardStatement />
      <p style={{ marginTop: 24, textAlign: 'center', fontSize: 11, color: R.muted }}>
        Still Waters Academy · Master Curriculum · stillwatersacademy.com
      </p>
    </PrintFrame>
  )
}